import { Email as EmailIcon } from "@mui/icons-material";
import { Backdrop, Button, Input, ListSubheader, MenuItem, Select } from "@mui/material";
import isEmpty from "lodash/isEmpty";
import type { FC } from "react";
import { useState } from "react";
import type { EmailTemplateEntry } from "../../misc/emailTemplates";
import { sendEmail } from "../../misc/sendEmail";
import type { SavedOrder } from "../orderTypes";
import type { OrderEmailType } from "../ordersApi";
import { useCustomerEmailTemplates } from "./useCustomerEmailTemplates";
import { useCustomerEmails } from "./useCustomerEmails";
import { useProviderEmailTemplates } from "./useProviderEmailTemplates";

const CustomerEmails: FC<{ orderId: string; onClose: () => void }> = ({ orderId, onClose }) => {
  const { sendEmail } = useCustomerEmails(orderId);

  const handleClick = async (type: OrderEmailType) => {
    await sendEmail(type);
    onClose();
  };
  return (
    <>
      <ListSubheader>Klient (wyślij wiadomość)</ListSubheader>
      <MenuItem onClick={() => handleClick("ORDER_TRANSFERRED")}>Przekazanie zamówienia</MenuItem>
      <MenuItem onClick={() => handleClick("CONFIRMATION_CREATED")}>Wyślij potwierdzenie</MenuItem>
      <MenuItem onClick={() => handleClick("CONFIRMATION_UPDATED")}>Wyślij potwierdzenie po zmianach</MenuItem>
      <MenuItem onClick={() => handleClick("PARTICIPANT_LIST_REMINDER")}>
        Wyślij przypomnienie o wysłaniu listy uczestników
      </MenuItem>
      <MenuItem onClick={() => handleClick("CHECK_IN")}>Wyślij odprawę</MenuItem>
    </>
  );
};

interface OptionGroupProps {
  readonly name: string;
  readonly templates: EmailTemplateEntry[];
  onClose: () => void;
}

const EmailTemplatesGroup: FC<OptionGroupProps> = ({ name, templates, onClose }) => {
  if (templates.length === 0) {
    return null;
  }
  return (
    <>
      <ListSubheader>{name}</ListSubheader>
      {templates.map((t, idx) => (
        <MenuItem
          value={idx}
          key={idx}
          onClick={() => {
            sendEmail(t.template);
            onClose();
          }}
        >
          {t.name}
        </MenuItem>
      ))}
    </>
  );
};

interface ProvidersOptionsProps {
  readonly templates: { providerName: string; emails: EmailTemplateEntry[] }[];
  onClose: () => void;
}

const ProvidersEmailOptions: FC<ProvidersOptionsProps> = ({ templates, onClose }) => {
  if (templates.length === 0) {
    return null;
  }
  return (
    <>
      {templates.map((t, idx) => (
        <EmailTemplatesGroup key={idx} name={t.providerName} templates={t.emails} onClose={onClose} />
      ))}
    </>
  );
};

interface Props {
  readonly order: SavedOrder;
}

const OrderEmailButton: FC<Props> = ({ order }) => {
  const [open, setOpen] = useState(false);
  const { templates: customerTemplates } = useCustomerEmailTemplates(order);
  const { templates: providerTemplates } = useProviderEmailTemplates(order);
  const disabled = isEmpty(customerTemplates) && isEmpty(providerTemplates);
  return (
    <Button
      variant="outlined"
      color="secondary"
      disabled={disabled}
      startIcon={<EmailIcon />}
      onClick={(e) => {
        e.stopPropagation();
        setOpen(true);
      }}
    >
      Email
      <Backdrop
        invisible
        open={open}
        onClick={(e) => {
          setOpen(false);
          e.stopPropagation();
        }}
      >
        <Select
          open={open}
          input={<Input style={{ width: 0, height: 0, visibility: "hidden" }} />}
          onClose={() => setOpen(false)}
          value={""}
        >
          <CustomerEmails orderId={order.id} onClose={() => setOpen(false)} />
          <EmailTemplatesGroup name="Klient (szablony)" templates={customerTemplates} onClose={() => setOpen(false)} />
          <ProvidersEmailOptions templates={providerTemplates} onClose={() => setOpen(false)} />
        </Select>
      </Backdrop>
    </Button>
  );
};

export default OrderEmailButton;
